import { ChangeEvent, useEffect, useState } from 'react'
import { AnimatePresence } from 'framer-motion'
import { faCheck, faEnvelope } from '@fortawesome/pro-duotone-svg-icons'

import Icon from '../icon/Icon'
import { useAuth } from '../../_hooks/use-auth'
import { useQuickPassProfile } from '../../_hooks/use-quickpass-profile'
import { requestInquiryEmail } from '../../_data/renter-llm'
import {
  TriggerButton,
  Backdrop,
  Card,
  Header,
  Title,
  CloseButton,
  Body,
  Field,
  FieldLabel,
  SubjectInput,
  BodyTextarea,
  Footer,
  Hint,
  FooterActions,
  SecondaryButton,
  PrimaryButton,
  StatusText,
  ErrorBanner,
  CopiedFlash,
} from './styled'
import { DrafterState, InquiryEmailDrafterProps } from './types'

const COPIED_FLASH_MS = 1500

/**
 * Modal that calls the inquiry-email LLM endpoint, lets the renter
 * tweak the subject + body, and supports copy-to-clipboard. Pulls the
 * renter's first/last name from auth so the sign-off is right.
 *
 * @param {InquiryEmailDrafterProps} props
 * @returns {JSX.Element}
 */
const InquiryEmailDrafter = ({
  listing,
}: InquiryEmailDrafterProps): JSX.Element => {
  const { quickPassUser } = useAuth()
  const { profile } = useQuickPassProfile()
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [state, setState] = useState<DrafterState>('idle')
  const [subject, setSubject] = useState<string>('')
  const [body, setBody] = useState<string>('')
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [didJustCopy, setDidJustCopy] = useState<boolean>(false)

  useEffect(() => {
    if (didJustCopy === false) {
      return
    }

    const handle = setTimeout(() => {
      setDidJustCopy(false)
    }, COPIED_FLASH_MS)

    return () => {
      clearTimeout(handle)
    }
  }, [didJustCopy])

  const handleOpen = async (): Promise<void> => {
    setIsOpen(true)
    setState('loading')
    setErrorMessage('')

    try {
      const renterPayload = {
        firstName: profile?.firstName ?? '',
        lastName: profile?.lastName ?? '',
        email: quickPassUser?.email ?? '',
      }

      const response = await requestInquiryEmail({
        listing,
        renter: renterPayload,
        preferences: profile?.unitPreferences ?? null,
      })

      if (response === null) {
        setState('offline')
        return
      }

      setSubject(response.subject)
      setBody(response.body)
      setState('ready')
    } catch (error) {
      setState('error')
      setErrorMessage(
        error instanceof Error
          ? error.message
          : 'Could not draft an email. Try again in a sec.',
      )
    }
  }

  const handleClose = (): void => {
    setIsOpen(false)
    setState('idle')
    setSubject('')
    setBody('')
    setDidJustCopy(false)
  }

  const handleSubjectChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setSubject(event.target.value)
  }

  const handleBodyChange = (event: ChangeEvent<HTMLTextAreaElement>): void => {
    setBody(event.target.value)
  }

  const handleCopy = async (): Promise<void> => {
    const composed = `Subject: ${subject}\n\n${body}`
    try {
      await navigator.clipboard.writeText(composed)
      setDidJustCopy(true)
    } catch (error) {
      console.error('[inquiry-email] clipboard failed', error)
    }
  }

  const stopPropagation = (event: { stopPropagation: () => void }): void => {
    event.stopPropagation()
  }

  return (
    <>
      <TriggerButton type="button" onClick={handleOpen}>
        <Icon icon={faEnvelope} /> Draft inquiry with AI
      </TriggerButton>

      <AnimatePresence>
        {isOpen === true ? (
          <Backdrop
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
          >
            <Card
              initial={{ opacity: 0, y: 20, scale: 0.97 }}
              animate={{
                opacity: 1,
                y: 0,
                scale: 1,
                transition: { duration: 0.28 },
              }}
              exit={{ opacity: 0, y: 20, scale: 0.97 }}
              onClick={stopPropagation}
            >
              <Header>
                <Title>Inquiry email draft</Title>
                <CloseButton
                  type="button"
                  onClick={handleClose}
                  aria-label="Close drafter"
                >
                  ×
                </CloseButton>
              </Header>

              <Body>
                {state === 'loading' ? (
                  <StatusText>Drafting it now…</StatusText>
                ) : null}

                {state === 'offline' ? (
                  <StatusText>
                    Backend not configured. The drafter needs the LLM endpoint
                    to be reachable.
                  </StatusText>
                ) : null}

                {state === 'error' ? (
                  <ErrorBanner>{errorMessage}</ErrorBanner>
                ) : null}

                {state === 'ready' ? (
                  <>
                    <Field>
                      <FieldLabel>Subject</FieldLabel>
                      <SubjectInput
                        value={subject}
                        onChange={handleSubjectChange}
                        aria-label="Email subject"
                      />
                    </Field>
                    <Field>
                      <FieldLabel>Body</FieldLabel>
                      <BodyTextarea
                        value={body}
                        onChange={handleBodyChange}
                        aria-label="Email body"
                      />
                    </Field>
                  </>
                ) : null}
              </Body>

              <Footer>
                <Hint>
                  Tweak whatever, then copy. Send from your own email.
                </Hint>
                <FooterActions>
                  {didJustCopy === true ? (
                    <CopiedFlash>
                      Copied <Icon icon={faCheck} />
                    </CopiedFlash>
                  ) : null}
                  <SecondaryButton type="button" onClick={handleClose}>
                    Close
                  </SecondaryButton>
                  <PrimaryButton
                    type="button"
                    onClick={handleCopy}
                    disabled={state !== 'ready'}
                  >
                    Copy email
                  </PrimaryButton>
                </FooterActions>
              </Footer>
            </Card>
          </Backdrop>
        ) : null}
      </AnimatePresence>
    </>
  )
}

export default InquiryEmailDrafter
